/**
 * Wijzig het ArchiMate-businesslaag-type van een «sharp.Concept» (§12.3, de
 * "vervangende" meta-operatie). Het Concept-stereotype blijft behouden dankzij
 * de polymorfe `applies_to`. Relaties die onder het nieuwe type ongeldig worden
 * (matrix) worden omgezet naar een gewone `archimate.Association` — de modelleur
 * krijgt dat vooraf te zien en bevestigt. Soft tot "definitief", conform §12.3.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMatrix, getRegistry, resolveBilingual } from "../../registry";
import { useWorkspaceData } from "../../state/workspace";

const BUSINESS_TYPES = [
  "archimate.BusinessObject",
  "archimate.BusinessActor",
  "archimate.BusinessRole",
  "archimate.BusinessProcess",
  "archimate.BusinessEvent",
  "archimate.BusinessFunction",
] as const;

const ASSOCIATION = "archimate.Association";

interface Props {
  workspaceId: string;
  branchId: string;
  conceptId: string;
  onClose: () => void;
}

export function ChangeArchimateTypeDialog({ workspaceId, branchId, conceptId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
  const reg = getRegistry();
  const matrix = getMatrix();
  const revision = useWorkspaceData((s) => s.revision);
  const store = useWorkspaceData.getState().store;
  const concept = store.getConcept(conceptId);
  const [target, setTarget] = useState<string>(concept?.typeId ?? "archimate.BusinessObject");
  const [busy, setBusy] = useState(false);

  const typeRef = (typeId: string) => {
    const et = reg.getElementType(typeId);
    // Matrix matcht op het base-type: een subtype heeft geen eigen matrix-regels.
    return et ? { id: reg.baseTypeIdFor(typeId), layer: et.layer, aspect: et.aspect } : null;
  };
  const typeLabel = (typeId: string) => {
    const et = reg.getElementType(typeId);
    return et ? resolveBilingual(et.label, lang) : typeId;
  };

  // Relaties aan dit concept die onder het gekozen type ongeldig worden en dus
  // naar een gewone Associatie worden omgezet.
  const impact = useMemo(() => {
    void revision;
    if (!concept || target === concept.typeId) return [] as { id: string; name: string }[];
    const newRef = typeRef(target);
    if (!newRef) return [];
    const affected: { id: string; name: string }[] = [];
    for (const r of store.listRelations()) {
      if (r.sourceConceptId !== conceptId && r.targetConceptId !== conceptId) continue;
      if (r.typeId === ASSOCIATION) continue; // al zo permissief als 't wordt
      const otherId = r.sourceConceptId === conceptId ? r.targetConceptId : r.sourceConceptId;
      const other = store.getConcept(otherId);
      const otherRef = other ? typeRef(other.typeId) : null;
      if (!otherRef) continue;
      const src = r.sourceConceptId === conceptId ? newRef : otherRef;
      const tgt = r.targetConceptId === conceptId ? newRef : otherRef;
      const outcome = matrix.validate(r.typeId, src, tgt);
      if (!outcome.valid) {
        const relName =
          resolveBilingual(r.label, lang) ||
          resolveBilingual(reg.getRelationType(r.typeId)?.label ?? {}, lang) ||
          r.typeId;
        affected.push({ id: r.id, name: relName });
      }
    }
    return affected;
  }, [target, revision, conceptId, lang]);

  const apply = async () => {
    if (!concept || target === concept.typeId) {
      onClose();
      return;
    }
    setBusy(true);
    const ws = useWorkspaceData.getState();
    // Eerst de relaties omzetten (concept-id nog stabiel), dan het concept-type
    // wijzigen — de copy-on-write-cascade remapt daarna schoon.
    for (const a of impact) {
      await ws.patchRelation(workspaceId, branchId, a.id, { type_id: ASSOCIATION });
    }
    await ws.patchConcept(workspaceId, branchId, conceptId, { type_id: target });
    setBusy(false);
    onClose();
  };

  if (!concept) return null;
  const conceptName = resolveBilingual(concept.name, lang) || conceptId;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationele backdrop; klik-buiten is muis-gemak, Escape + sluitknop zijn het toegankelijke pad
    <div
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        className="w-[440px] max-w-[92vw] rounded-lg border border-(--ld-control) bg-(--ld-surface) p-4 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="mb-1 font-semibold text-(--ld-text) text-sm">
          {t("changeArchimateType.title")}
        </h2>
        <p className="mb-3 text-(--ld-muted) text-xs">
          {t("changeArchimateType.intro", { name: conceptName })}
        </p>

        <div className="mb-3 flex flex-col gap-1">
          {BUSINESS_TYPES.map((typeId) => (
            <label
              key={typeId}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-(--ld-raised)"
            >
              <input
                type="radio"
                name="ae-type"
                checked={target === typeId}
                onChange={() => setTarget(typeId)}
              />
              <span className="text-(--ld-text)">{typeLabel(typeId)}</span>
              {concept.typeId === typeId && (
                <span className="rounded bg-(--ld-active) px-1.5 py-0.5 text-xs text-(--ld-muted)">
                  {t("changeArchimateType.currentBadge")}
                </span>
              )}
            </label>
          ))}
        </div>

        {target !== concept.typeId && (
          <div className="mb-3 rounded border border-transparent bg-(--ld-notice-bg) p-2 text-xs">
            {impact.length === 0 ? (
              <span className="text-(--ld-muted)">{t("changeArchimateType.noImpact")}</span>
            ) : (
              <>
                <div className="mb-1 font-medium text-(--ld-notice-ink)">
                  {t("changeArchimateType.impactWarning", { count: impact.length })}
                </div>
                <ul className="list-disc pl-4 text-(--ld-notice-ink)">
                  {impact.map((a) => (
                    <li key={a.id} className="break-words">
                      {a.name}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-(--ld-control) px-3 py-1 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
            onClick={onClose}
            disabled={busy}
          >
            {t("changeArchimateType.cancel")}
          </button>
          <button
            type="button"
            className="rounded bg-(--ld-primary) px-3 py-1 text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
            onClick={() => void apply()}
            disabled={busy || target === concept.typeId}
          >
            {busy ? t("changeArchimateType.applying") : t("changeArchimateType.apply")}
          </button>
        </div>
      </div>
    </div>
  );
}
